'use client';

import { forwardRef } from 'react';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { EDITOR_HOST_CLASS_PROP } from '@/hen-ui/catalog/editor-host';
import {
  useMenuContentCapsInline,
  useMenuContentMatch,
  type MenuContentPlacement,
} from '@/hen-ui/composites/SubNav/MenuContentSlot';
import { cn } from '@/hen-ui/utils';
import { Flex } from '../Flex';

export interface NavbarMenuContentProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  /**
   * Label of the top-level menu this content belongs to, compared without
   * case or surrounding spaces. Leave blank to show it in every menu.
   * The editor offers the parent Navbar's menus instead of a free-text name.
   */
  menu?: string;
  /**
   * Where the content sits on larger screens. `divided` (default) keeps the
   * side column behind a line; `inline` places it right after the link
   * columns, level with their headings, with no line. In the desktop panel,
   * inline content is as wide as `--cds-navbar-menu-content-width` allows
   * (no cap when unset); the small-screen drawer does not cap it.
   */
  placement?: MenuContentPlacement;
  /** Content shown in the menu's side column. */
  children?: React.ReactNode;
}

function partitionDomProps(
  props: Omit<NavbarMenuContentProps, 'menu' | 'placement' | 'children'>,
): {
  domProps: Record<string, unknown>;
  editorHostClassName?: string;
} {
  const record = props as Record<string, unknown>;
  const editorHostClassName = record[EDITOR_HOST_CLASS_PROP];
  const { className: _className, ...rest } = record;
  delete rest[EDITOR_HOST_CLASS_PROP];
  return {
    domProps: rest,
    editorHostClassName:
      typeof editorHostClassName === 'string' ? editorHostClassName : undefined,
  };
}

/**
 * Authored content for one Navbar drop-down menu. Place inside the Navbar
 * `menuContent` slot; it renders in the side column of the menu whose label
 * matches `menu` on desktop, and on that menu's tier in the mobile drawer.
 * Outside a Navbar menu it renders its children as a plain stack.
 */
export const NavbarMenuContent = forwardRef<
  HTMLDivElement,
  NavbarMenuContentProps
>(({ menu, placement, children, ...props }, ref) => {
  const matches = useMenuContentMatch(
    menu,
    placement === 'inline' ? 'inline' : 'divided',
  );
  const capsInline = useMenuContentCapsInline();
  if (matches === false) return null;

  const { domProps, editorHostClassName } = partitionDomProps(props);
  const menuName = menu?.trim();
  const className = cn(
    placement === 'inline' && capsInline && 'max-w-nav-menu-content',
    editorHostClassName,
  );
  return (
    <Flex
      ref={ref}
      direction='column'
      gap='sm'
      data-navbar-menu={menuName || undefined}
      {...domProps}
      {...(className ? { className } : {})}
      data-hen-id={COMPONENT_IDS.NavbarMenuContent}
    >
      {children}
    </Flex>
  );
});

NavbarMenuContent.displayName = 'NavbarMenuContent';
